import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { MicroServiceHttpService } from './microServiceHttp.service';
import { isPrismNewUrl } from './bot-builder-routing';
import { Observable } from 'rxjs';

export interface WhitelabelChatUsage {
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
}

export interface WhitelabelChatToolCall {
  stepIndex: number;
  input: any;
  output: any;
  error: string | null;
  durationMs: number | null;
}

export interface WhitelabelChatTool {
  toolName: string;
  toolPid: number | null;
  callCount: number;
  totalDurationMs: number;
  calls: WhitelabelChatToolCall[];
}

export interface WhitelabelChatNode {
  executionNodePid: number;
  nodePid: number;
  nodeName: string;
  depth: number;
  executionOrder: number;
  calledByNodePid: number | null;
  calledByNodeName: string | null;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  isError: boolean;
  errorMessage: string | null;
  tools: WhitelabelChatTool[];
}

export interface WhitelabelChatTrace {
  traceId: string;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  toolCallCount: number;
  nodeCount: number;
  isEscalated: boolean;
  isError: boolean;
  nodes: WhitelabelChatNode[];
}

export interface WhitelabelChatMessage {
  id: number;
  role: 'user' | 'assistant' | 'system' | 'ai';
  content: string;
  createdAt: string;
  sessionId: string;
  trace?: WhitelabelChatTrace;
}

export interface WhitelabelChatMessagesData {
  messages: WhitelabelChatMessage[];
  documents?: WhitelabelSessionDocument[];
  documentCount?: number;
  totalDocumentSize?: number;
  total: number;
  page: number;
  hasMore: boolean;
}

export interface WhitelabelChatMessagesResponse {
  success: boolean;
  data: WhitelabelChatMessagesData;
  requestId: string;
}

export interface WhitelabelChatCompletePayload {
  answer: string;
  usage?: WhitelabelChatUsage;
}

export interface WhitelabelChatHandlers {
  onDelta?: (chunk: string) => void;
  onProgress?: (agent: string) => void;
  onComplete: (payload: WhitelabelChatCompletePayload) => void;
  onError: (err: Error) => void;
}

export interface WhitelabelSessionDocumentUploadResult {
  fileName: string;
  chunkCount: number;
  publicUrl: string;
  downloadUrl?: string | null;
  s3Key?: string;
  contentType?: string | null;
  size: number;
}

export interface WhitelabelSessionDocumentUploadResponse {
  collectionName: string;
  sessionId: string;
  documents: WhitelabelSessionDocumentUploadResult[];
}

interface SessionDocumentUploadEnvelope {
  success?: boolean;
  data?: WhitelabelSessionDocumentUploadResponse;
}

export interface WhitelabelSessionDocument {
  id: number;
  chatbotId: number;
  sessionId: string;
  userId?: number | null;
  fileName: string;
  contentType?: string | null;
  size: number;
  s3Key: string;
  publicUrl: string;
  downloadUrl?: string | null;
  qdrantCollectionName: string;
  chunkCount: number;
  uploadStatus: 'uploading' | 'completed' | 'failed';
  createdAt: string;
}

/** Wrapped JSON when streaming is disabled for the chatbot. */
interface StaticChatEnvelope {
  success?: boolean;
  data?: {
    answer?: string;
    usage?: WhitelabelChatUsage;
  };
}

export interface WhitelabelChatSource {
  source: string | null;
  sessionCount: number;
  sessionIds: string[];
  lastActivityAt: string;
  totalTokens: number;
  totalUserMessages: number;
  totalAiResponses: number;
  sessionStats: {
    sessionId: string;
    tokensUsed: number;
    userMessageCount: number;
    aiResponseCount: number;
  }[];
}

export interface WhitelabelChatSourcesData {
  chatbotId: number;
  chatbotTotalTokens: number;
  chatbotTotalUserMessages: number;
  chatbotTotalAiResponses: number;
  sources: WhitelabelChatSource[];
  total: number;
  page: number;
  hasMore: boolean;
}

export interface WhitelabelChatSourcesResponse {
  success: boolean;
  data: WhitelabelChatSourcesData;
  requestId: string;
}

export interface AgentPromptTurn {
  question: string;
  response: string;
  skipQuestion?: boolean;
}

@Injectable({ providedIn: 'root' })
export class WhitelabelChatService {
  private apiService = inject(ApiService);
  private microHttp = inject(MicroServiceHttpService);

  // ── Global Analysis Drawer State ──────────────────────────────────────────
  readonly isAnalysisDrawerOpen = signal(false);
  readonly selectedSource = signal<WhitelabelChatSource | null>(null);
  readonly selectedChatbotId = signal<number | null>(null);
  readonly selectedSessionId = signal<string | null>(null);
  readonly sessionMessages = signal<WhitelabelChatMessage[]>([]);
  readonly sessionDocuments = signal<WhitelabelSessionDocument[]>([]);
  readonly sessionDocumentCount = signal<number>(0);
  readonly sessionTotalDocumentSize = signal<number>(0);
  readonly isMessagesLoading = signal(false);

  openAnalysisDrawer(source: WhitelabelChatSource, chatbotId: number): void {
    this.selectedSource.set(source);
    this.selectedChatbotId.set(chatbotId);
    this.selectedSessionId.set(null);
    this.sessionMessages.set([]);
    this.sessionDocuments.set([]);
    this.sessionDocumentCount.set(0);
    this.sessionTotalDocumentSize.set(0);
    this.isAnalysisDrawerOpen.set(true);

    // Auto-select first session if available
    if (source.sessionStats && source.sessionStats.length > 0) {
      this.selectSession(source.sessionStats[0].sessionId);
    }
  }

  closeAnalysisDrawer(): void {
    this.isAnalysisDrawerOpen.set(false);
    // Delay clearing data slightly for smooth exit animation
    setTimeout(() => {
      this.selectedSource.set(null);
      this.selectedChatbotId.set(null);
      this.selectedSessionId.set(null);
      this.sessionMessages.set([]);
      this.sessionDocuments.set([]);
      this.sessionDocumentCount.set(0);
      this.sessionTotalDocumentSize.set(0);
    }, 400);
  }

  selectSession(sessionId: string): void {
    this.selectedSessionId.set(sessionId);
    this.isMessagesLoading.set(true);
    this.sessionMessages.set([]);
    this.sessionDocuments.set([]);
    this.sessionDocumentCount.set(0);
    this.sessionTotalDocumentSize.set(0);

    const chatbotId = this.selectedChatbotId() ?? undefined;
    const source = this.selectedSource()?.source ?? undefined;
    this.getSessionMessages(sessionId, chatbotId, source).subscribe({
      next: (res) => {
        if (this.selectedSessionId() === sessionId) {
          this.sessionMessages.set(res.data.messages || []);
          this.sessionDocuments.set(res.data.documents || []);
          this.sessionDocumentCount.set(res.data.documentCount ?? (res.data.documents?.length ?? 0));
          this.sessionTotalDocumentSize.set(res.data.totalDocumentSize ?? 0);
        }
      },
      error: () => {
        if (this.selectedSessionId() === sessionId) {
          this.sessionMessages.set([]);
          this.sessionDocuments.set([]);
          this.sessionDocumentCount.set(0);
          this.sessionTotalDocumentSize.set(0);
        }
      },
      complete: () => {
        if (this.selectedSessionId() === sessionId) {
          this.isMessagesLoading.set(false);
        }
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────────

  /** Chat endpoint (`subPath` e.g. `/upload-document`): prism-new when routed, else the gateway. */
  private resolveChatUrl(subPath = ''): string {
    const path = `chat${subPath}`;
    const routed = this.microHttp.whitelabelFetchUrl(path);
    if (isPrismNewUrl(routed)) {
      return routed;
    }
    const env = environment as typeof environment & {
      workspaceWhitelabelChatUrl?: string;
    };
    if (env.workspaceWhitelabelChatUrl?.trim()) {
      return `${env.workspaceWhitelabelChatUrl.trim()}${subPath}`;
    }
    const base = environment.microAppService_url.whitelabel.url.replace(/\/$/, '');
    return `${base}/${path}`;
  }

  private getCsrfToken(): string | null {
    for (const cookie of document.cookie.split(';')) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'prism-auth_csrf_token') {
        return decodeURIComponent(value ?? '');
      }
    }
    return null;
  }

  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/event-stream;q=0.9, */*;q=0.8',
    };
    const accessToken = this.apiService.getAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const csrf = this.getCsrfToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
    const deviceId = this.apiService.getDeviceId();
    if (deviceId) {
      headers['x-device-id'] = deviceId;
    }
    return headers;
  }

  /**
   * POST whitelabel chat. Server returns JSON when streaming is off, or SSE (`data:` lines) when on.
   */
  async sendMessage(
    query: string,
    chatbotId: number,
    sessionId: string,
    handlers: WhitelabelChatHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const url = this.resolveChatUrl();
    const body = JSON.stringify({
      query,
      chatbot_id: chatbotId,
      session_id: sessionId,
    });

    try {
      const response = await this.microHttp.fetchWhitelabel(url, {
        method: 'POST',
        headers: this.buildHeaders(),
        body,
        credentials: 'include',
        signal,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => response.statusText);
        throw new Error(errText || `Chat request failed (${response.status})`);
      }

      const contentType = response.headers.get('content-type') ?? '';

      if (contentType.includes('application/json')) {
        const json = (await response.json()) as StaticChatEnvelope;
        const answer =
          json?.data?.answer ??
          (json as unknown as { answer?: string })?.answer ??
          '';
        if (!answer) {
          throw new Error('Chat response had no answer.');
        }
        handlers.onComplete({
          answer,
          usage: json?.data?.usage,
        });
        return;
      }

      await this.readSseStream(response, handlers);
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') {
        return;
      }
      const err = e instanceof Error ? e : new Error(String(e));
      handlers.onError(err);
    }
  }

  /**
   * Uploads session-scoped documents to the whitelabel chat upload endpoint.
   */
  async uploadSessionDocuments(
    chatbotId: number,
    sessionId: string,
    files: File[],
    options?: { userId?: number | string | null; source?: string | null },
    signal?: AbortSignal,
  ): Promise<WhitelabelSessionDocumentUploadResponse> {
    const form = new FormData();
    form.append('chatbot_id', String(chatbotId));
    form.append('session_id', sessionId);
    if (options?.userId !== undefined && options.userId !== null) {
      form.append('user_id', String(options.userId));
    }
    if (options?.source) {
      form.append('source', options.source);
    }
    for (const file of files) {
      form.append('files', file, file.name);
    }

    const headers = this.buildHeaders();
    delete headers['Content-Type'];

    const response = await this.microHttp.fetchWhitelabel(this.resolveChatUrl('/upload-document'), {
      method: 'POST',
      headers,
      body: form,
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      throw new Error(await this.readUploadError(response));
    }

    const json = (await response.json()) as
      | SessionDocumentUploadEnvelope
      | WhitelabelSessionDocumentUploadResponse;
    return this.normalizeUploadResponse(json);
  }

  /**
   * Unwraps direct and gateway-wrapped upload response envelopes.
   */
  private normalizeUploadResponse(
    payload: unknown,
  ): WhitelabelSessionDocumentUploadResponse {
    let current = payload;

    for (let depth = 0; depth < 4; depth += 1) {
      if (!current || typeof current !== 'object') break;

      const record = current as Record<string, unknown>;
      if (record['success'] === false) {
        throw new Error(this.extractUploadError(record));
      }

      if (Array.isArray(record['documents'])) {
        return record as unknown as WhitelabelSessionDocumentUploadResponse;
      }

      if ('data' in record && record['data']) {
        current = record['data'];
        continue;
      }

      break;
    }

    throw new Error('Document upload response was incomplete.');
  }

  /**
   * Reads a safe upload error message from a failed HTTP response.
   */
  private async readUploadError(response: Response): Promise<string> {
    const fallback =
      response.status === 408
        ? 'Document upload took too long. Please try smaller files or upload fewer files at once.'
        : `Document upload failed (${response.status})`;
    const raw = await response.text().catch(() => '');
    if (!raw) return fallback;

    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return this.extractUploadError(parsed) || fallback;
    } catch {
      return fallback;
    }
  }

  /**
   * Extracts a short user-facing upload error from gateway/API envelopes.
   */
  private extractUploadError(payload: Record<string, unknown>): string {
    const code = typeof payload['code'] === 'string' ? payload['code'] : '';
    if (code === 'GATEWAY_TIMEOUT') {
      return 'Document upload took too long. Please try smaller files or upload fewer files at once.';
    }

    const error = typeof payload['error'] === 'string' ? payload['error'] : '';
    const message =
      typeof payload['message'] === 'string' ? payload['message'] : '';
    return error || message || 'Document upload failed. Please try again.';
  }

  saveAssistantIframeConfig(payload: any, showToast = true, pid?: number): Observable<any> {
    const url = pid ? `/assistant-iframe-config/${pid}` : '/assistant-iframe-config';
    const method = pid ? 'patch' : 'post';
    return this.microHttp[method]('whitelabel', url, payload, {
      successMessage: 'Configuration saved successfully',
      errorMessage: 'Failed to save configuration',
      showToast: showToast
    });
  }

  /**
   * Reliable "fire-and-forget" save using native fetch with keepalive.
   * Useful for saving on browser reload/unload where standard HttpClient is aborted.
   */
  async saveAssistantIframeConfigSync(payload: any, pid?: number): Promise<void> {
    try {
      const path = pid ? `assistant-iframe-config/${pid}` : 'assistant-iframe-config';
      const url = this.microHttp.whitelabelFetchUrl(path);

      const headers = this.buildHeaders();
      // Ensure we have the latest token from session storage if needed
      const token = sessionStorage.getItem('access_token');
      if (token && !headers['Authorization']) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await this.microHttp.fetchWhitelabel(url, {
        method: pid ? 'PATCH' : 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true, // Crucial for saving on unload
        credentials: 'include'
      });
    } catch (err) {
      console.warn('Sync save failed (expected if closing)', err);
    }
  }

  getAssistantIframeConfig(chatbotId: number): Observable<any> {
    return this.microHttp.get('whitelabel', `/assistant-iframe-config/chatbot/${chatbotId}`, {showToast: false});
  }

  getChatbotSources(chatbotId: number, page: number = 1, limit: number = 20): Observable<WhitelabelChatSourcesResponse> {
    return this.microHttp.get<WhitelabelChatSourcesResponse>('whitelabel', 'chat/sources', {
      params: { chatbot_id: chatbotId, page, limit },
      showToast: false
    });
  }

  getSessionMessages(
    sessionId: string,
    chatbotId?: number,
    source?: string,
    page: number = 1,
    limit: number = 20,
  ): Observable<WhitelabelChatMessagesResponse> {
    const params: Record<string, string | number | boolean> = {
      session_id: sessionId,
      page,
      limit,
    };
    if (chatbotId !== undefined && chatbotId !== null) {
      params['chatbot_id'] = chatbotId;
    }
    if (source) {
      params['source'] = source;
    }

    return this.microHttp.get<WhitelabelChatMessagesResponse>('whitelabel', 'chat/sessions/messages', {
      params,
      showToast: false
    });
  }

  private async readSseStream(
    response: Response,
    handlers: WhitelabelChatHandlers,
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable.');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let assembled = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const rawLine of lines) {
          const line = rawLine.replace(/\r$/, '');
          if (!line.startsWith('data:')) {
            continue;
          }
          const payload = line.slice(5).trimStart();
          if (payload === '[DONE]') {
            continue;
          }

          let parsed: Record<string, unknown>;
          try {
            parsed = JSON.parse(payload) as Record<string, unknown>;
          } catch {
            continue;
          }

          // prism-new reports a failure after the stream opened as `{ error: true, message }`.
          if (parsed['error'] === true) {
            throw new Error(typeof parsed['message'] === 'string' ? parsed['message'] : 'Chat request failed.');
          }

          if (parsed['progress'] === true && typeof parsed['agent'] === 'string') {
            handlers.onProgress?.(parsed['agent'] as string);
            continue;
          }

          if (typeof parsed['delta'] === 'string') {
            const d = parsed['delta'] as string;
            assembled += d;
            handlers.onDelta?.(d);
            continue;
          }

          if (parsed['done'] === true) {
            const answer =
              typeof parsed['answer'] === 'string' ? (parsed['answer'] as string) : assembled;
            handlers.onComplete({
              answer,
              usage: parsed['usage'] as WhitelabelChatUsage | undefined,
            });
            return;
          }
        }
      }

      if (assembled.trim()) {
        handlers.onComplete({ answer: assembled });
      } else {
        throw new Error('Stream ended without an answer.');
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * multi-turn SSE for Agent System Prompt Generation.
   */
  async generateAgentSystemPrompt(
    payload: { chatbotId: number; sessionId?: string; information?: AgentPromptTurn[]; userId?: number },
    handlers: {
      onQuestion?: (questions: string[]) => void;
      onDelta?: (chunk: string) => void;
      onComplete: (content: string) => void;
      onError: (err: Error) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const url = this.microHttp.whitelabelFetchUrl('agent-system-prompt/generate');

    try {
      const response = await this.microHttp.fetchWhitelabel(url, {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(payload),
        credentials: 'include',
        signal,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => response.statusText);
        throw new Error(errText || `System Prompt Request failed (${response.status})`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Response body is not readable.');

      const decoder = new TextDecoder();
      let buffer = '';
      let assembledContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const rawLine of lines) {
          const line = rawLine.replace(/\r$/, '');
          if (!line.startsWith('data:')) continue;

          const data = line.slice(5).trimStart();
          if (data === '[DONE]') continue;

          let parsed: any;
          try {
            parsed = JSON.parse(data);
          } catch (e) {
            console.error('SSE Parse Error', e, data);
            continue;
          }
          // prism-new reports a failure after the stream opened as `{ error: true, message }`.
          if (parsed.error === true) {
            throw new Error(typeof parsed.message === 'string' ? parsed.message : 'Prompt generation failed.');
          }
          try {
            if (parsed.type === 'QUESTION' && parsed.questions) {
              handlers.onQuestion?.(parsed.questions);
            } else if (parsed.type === 'PROMPT' && (typeof parsed.content === 'string' || typeof parsed.delta === 'string')) {
              const delta = parsed.content ?? parsed.delta;
              assembledContent += delta;
              handlers.onDelta?.(delta);
            }
          } catch (e) {
            console.error('SSE Parse Error', e, data);
          }
        }
      }

      if (assembledContent.trim()) {
        handlers.onComplete(assembledContent);
      }
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      handlers.onError(e);
    }
  }
}
